import { all, takeEvery, takeLatest, takeLeading, put, call, select } from 'redux-saga/effects'
import { notification } from 'antd'
import { history } from 'index'
import { getEnvParam } from 'env'

import { getSearchParams, makeSearchString } from 'utils'
import { getLoginFailureRedirect } from 'libs/userAgent'

import * as landing from 'services/landing'
import type { EntryLoginResult } from 'services/landing'
import ouCalendarService from 'services/ouCalendar'
import { closeFunctionPopups } from 'services/popupCleanup'
import actions from './actions'
import {
  UserAction,
  // LoginPayload,
  // LoginByKeyPayload,
  // LoginByTokenPayload,
  // LoginByEntryPayload,
  // RegisterPayload,
  // LogoutPayload,
  AuthProvidersMap,
  CurrentAccountResponse,
} from './types'
import { RootState } from '../types'

function isValidRedirectPath(path: string): boolean {
  if (!path.startsWith('/')) return false
  if (path.startsWith('//')) return false
  if (path.includes('://')) return false
  if (path.toLowerCase().includes('javascript:')) return false
  return true
}

const mapAuthProviders: AuthProvidersMap = {
  landing: {
    login: landing.login,
    loginByKey: landing.loginByKey,
    loginByToken: landing.loginByToken,
    loginByEntryCheck: landing.loginByEntryCheck,
    loginByTokenFromDesktop: landing.loginByTokenFromDesktop,
    loginByEntry: landing.loginByEntry,
    register: landing.register,
    currentAccount: landing.currentAccount as unknown as () => Promise<
      CurrentAccountResponse | false
    >,
    logout: landing.logout,
  },
}

export function* LOGIN(action: UserAction) {
  if (action.type !== 'user/LOGIN') return

  const { email, password } = action.payload
  yield put({
    type: 'user/SET_STATE',
    payload: {
      loading: true,
    },
  })
  const { authProvider: autProviderName }: { authProvider: keyof AuthProvidersMap } = yield select(
    (state: RootState) => state.appsettings,
  )
  const result: string = yield call(mapAuthProviders[autProviderName].login, email, password)
  if (result === 'success') {
    yield call(INNER_LOAD_CURRENT_ACCOUNT)

    const onLoadAccount = () => {
      const params = getSearchParams()
      const defaultPage = '/'
      const decoded =
        params.path != null ? window.decodeURIComponent(String(params.path)) : defaultPage
      const target = isValidRedirectPath(decoded) ? decoded : defaultPage
      history.push(target)
    }

    yield call(onLoadAccount)
  }

  yield put({
    type: 'user/SET_STATE',
    payload: {
      loading: false,
    },
  })
}

export function* LOGIN_BY_KEY(action: UserAction) {
  if (action.type !== 'user/LOGIN_BY_KEY') return

  const { key } = action.payload
  yield put({
    type: 'user/SET_STATE',
    payload: {
      loading: true,
    },
  })
  const { authProvider: autProviderName }: { authProvider: keyof AuthProvidersMap } = yield select(
    (state: RootState) => state.appsettings,
  )
  const result: string = yield call(mapAuthProviders[autProviderName].loginByKey!, key)
  if (result === 'success') {
    yield call(INNER_LOAD_CURRENT_ACCOUNT)

    const onLoadAccount = () => {
      const params = getSearchParams()
      const defaultPage = '/'
      const decoded =
        params.path != null ? window.decodeURIComponent(String(params.path)) : defaultPage
      const target = isValidRedirectPath(decoded) ? decoded : defaultPage
      history.push(target)
    }

    yield call(onLoadAccount)
  }

  yield put({
    type: 'user/SET_STATE',
    payload: {
      loading: false,
    },
  })

  if (result !== 'success') {
    // OfficeChat webview에서는 재로그인이 불가하므로 재실행 안내 화면으로 보낸다 (OFM-229)
    yield call(history.push, getLoginFailureRedirect())
  }
}

export function* LOGIN_BY_TOKEN(action: UserAction) {
  if (action.type !== 'user/LOGIN_BY_TOKEN') return

  const { tokenInfo } = action.payload
  yield put({
    type: 'user/SET_STATE',
    payload: {
      loading: true,
    },
  })
  const { authProvider: autProviderName }: { authProvider: keyof AuthProvidersMap } = yield select(
    (state: RootState) => state.appsettings,
  )
  const result: string = yield call(mapAuthProviders[autProviderName].loginByToken!, tokenInfo)
  if (result === 'success') {
    yield call(INNER_LOAD_CURRENT_ACCOUNT)

    const onLoadAccount = () => {
      if (window.location.pathname.indexOf('/auth/') !== 0) {
        return
      }
      const searchParams = getSearchParams()
      const defaultPage = '/'
      let target = defaultPage
      if (searchParams.path != null) {
        const decoded = window.decodeURIComponent(String(searchParams.path))
        if (isValidRedirectPath(decoded)) {
          target = decoded
          if (searchParams.params != null && typeof searchParams.params === 'string') {
            const params = JSON.parse(atob(searchParams.params))
            // 로그인이 성공했으므로, login_hint는 제거.
            if (params.email != null) {
              delete params.email
            }
            const newParams = makeSearchString(params)
            if (newParams.length > 0) {
              target = `${target}?${newParams}`
            }
          }
        }
      }

      history.push(target)
      // history.navigate(target, { replace: true })
      // window.location.replace(target);
    }

    yield call(onLoadAccount)
  }

  yield put({
    type: 'user/SET_STATE',
    payload: {
      loading: false,
    },
  })

  if (result !== 'success') {
    // OfficeChat webview에서는 재로그인이 불가하므로 재실행 안내 화면으로 보낸다 (OFM-229)
    yield call(history.push, getLoginFailureRedirect())
  }
}

export function* LOGIN_BY_ENTRY_CHECK(action: UserAction) {
  if (action.type !== 'user/LOGIN_BY_ENTRY_CHECK') return

  const { token, path, additionalParams } = action.payload
  const { authProvider: autProviderName }: { authProvider: keyof AuthProvidersMap } = yield select(
    (state: RootState) => state.appsettings,
  )
  const func = mapAuthProviders[autProviderName].loginByEntryCheck!
  const result: boolean = yield call(func, token, path)
  if (result === true) {
    const onLoadAccount = () => {
      const defaultPage = '/'
      const decoded = path != null ? window.decodeURIComponent(path) : defaultPage
      let target = isValidRedirectPath(decoded) ? decoded : defaultPage
      if (additionalParams != null) {
        target += `?${additionalParams.join('&')}`
      }
      history.push(target)
    }
    yield call(LOAD_CURRENT_ACCOUNT) // wave token으로 접속시 redirect_path 를 currentAccount()에 전달하기 위해 한번더 호출.
    yield call(onLoadAccount)
  } else if (
    yield call(
      mapAuthProviders[autProviderName].loginByTokenFromDesktop!,
      token,
      path,
      additionalParams,
    )
  ) {
    const onLoadAccount = () => {
      const defaultPage = '/'
      const decoded = path != null ? window.decodeURIComponent(path) : defaultPage
      let target = isValidRedirectPath(decoded) ? decoded : defaultPage
      if (additionalParams != null) {
        target += `?${additionalParams.join('&')}`
      }
      history.push(target)
    }
    yield call(LOAD_CURRENT_ACCOUNT) // wave token으로 접속시 redirect_path 를 currentAccount()에 전달하기 위해 한번더 호출.
    yield call(onLoadAccount)
  } else {
    yield call(LOGIN_BY_ENTRY, action)
  }
}

export function* LOGIN_BY_ENTRY(action: UserAction) {
  if (action.type !== 'user/LOGIN_BY_ENTRY' && action.type !== 'user/LOGIN_BY_ENTRY_CHECK') return

  const { token, path, additionalParams } = action.payload
  yield put({
    type: 'user/SET_STATE',
    payload: {
      loading: true,
    },
  })
  const { authProvider: autProviderName }: { authProvider: keyof AuthProvidersMap } = yield select(
    (state: RootState) => state.appsettings,
  )
  const result: EntryLoginResult = yield call(
    mapAuthProviders[autProviderName].loginByEntry!,
    token,
  )
  if (result === 'success') {
    yield call(INNER_LOAD_CURRENT_ACCOUNT)

    const onLoadAccount = () => {
      const defaultPage = '/'
      const decoded = path != null ? window.decodeURIComponent(path) : defaultPage
      let target = isValidRedirectPath(decoded) ? decoded : defaultPage
      if (additionalParams != null) {
        target += `?${additionalParams.join('&')}`
      }
      history.push(target)
    }

    yield call(onLoadAccount)
  }

  yield put({
    type: 'user/SET_STATE',
    payload: {
      loading: false,
    },
  })

  if (result !== 'success') {
    // entry_token 교환 실패(만료/소진/무효 토큰 등). 기존(만료) 세션이 storage에 남아 있으면
    // layouts의 isUserAuthorized(= user.authorized || app.user.token != null)가 true로 유지되어
    // /auth/login 진입 시 곧바로 /main 으로 되튕긴다(무한 로딩). forced logout으로 stale 세션을 정리한다.
    // forced=true 는 logout 내부의 entryToken 기반 LOGIN_BY_ENTRY 재dispatch 경로를 타지 않아 이중 진입을 막는다.
    yield call(mapAuthProviders[autProviderName].logout, true)
  }

  // 409/400 에러는 entry 페이지가 모달 확인 후 직접 /auth/login으로 이동하므로 여기서 push 하지 않는다.
  // EntryLoginResult union에서 'success'/'fail' 문자열이 아니면 EntryLoginError 객체.
  if (typeof result !== 'string') {
    yield put({ type: 'user/SET_ENTRY_ERROR', payload: result })
    return
  }

  if (result !== 'success') {
    // OfficeChat webview에서는 재로그인이 불가하므로 재실행 안내 화면으로 보낸다 (OFM-229)
    yield call(history.push, getLoginFailureRedirect())
  }
}

export function* REGISTER(action: UserAction) {
  if (action.type !== 'user/REGISTER') return

  const { email, password, name } = action.payload
  yield put({
    type: 'user/SET_STATE',
    payload: {
      loading: true,
    },
  })
  const { authProvider }: { authProvider: keyof AuthProvidersMap } = yield select(
    (state: RootState) => state.appsettings,
  )
  const success: boolean = yield call(
    mapAuthProviders[authProvider].register,
    email,
    password,
    name,
  )
  if (success) {
    yield call(INNER_LOAD_CURRENT_ACCOUNT)

    yield call(history.push, '/')
    notification.success({
      message: 'Succesful Registered',
      description: 'You have successfully registered!',
    })
  }

  yield put({
    type: 'user/SET_STATE',
    payload: {
      loading: false,
    },
  })
}

function* INNER_LOAD_CURRENT_ACCOUNT() {
  try {
    const { authProvider }: { authProvider: keyof AuthProvidersMap } = yield select(
      (state: RootState) => state.appsettings,
    )
    const response: CurrentAccountResponse | false = yield call(
      mapAuthProviders[authProvider].currentAccount,
    )
    if (response) {
      const { id, email, name, avatar, role, workspace, serverType } = response
      yield put({
        type: 'user/SET_STATE',
        payload: {
          id,
          name,
          email,
          avatar: avatar || '',
          role,
          authorized: true,
          workspace,
          serverType,
        },
      })
      // 회사캘린더 OU 목록 프리페치. dedupe 내장이라 중복 호출 안전하며,
      // 실패해도 throw하지 않으므로 saga를 블로킹하지 않는 fire-and-forget으로 호출한다.
      void ouCalendarService.load()
    }
  } catch {
    yield put({
      type: 'user/SET_STATE',
      payload: {
        authorized: false,
      },
    })
  }
}

export function* LOAD_CURRENT_ACCOUNT() {
  yield put({
    type: 'user/SET_STATE',
    payload: {
      loading: true,
    },
  })

  yield call(INNER_LOAD_CURRENT_ACCOUNT)

  // currentAccount가 실패하여 비인가 상태이고, /auth/ 경로가 아닌 경우 LOGOUT 처리.
  const { authorized }: { authorized: boolean } = yield select((state: RootState) => state.user)
  if (authorized === false && window.location.pathname.indexOf('/auth/') !== 0) {
    yield put({ type: 'user/LOGOUT' })
  }

  yield put({
    type: 'user/SET_STATE',
    payload: {
      loading: false,
    },
  })
}

export function* LOGOUT(action: UserAction) {
  if (action.type !== 'user/LOGOUT') return

  // 강제 로그아웃 포함 로그아웃 시 열려 있던 기능 팝업(설정/이동/조직도/일정발송 등)을 닫는다.
  // error/warning 모달(예: biz 접근 차단 안내)은 대상에서 제외되어 유지된다. (OMD-27)
  closeFunctionPopups()

  // 다른 계정으로 재로그인할 때 이전 계정의 OU 캐시가 남지 않도록 초기화.
  ouCalendarService.reset()

  const { authProvider }: { authProvider: keyof AuthProvidersMap } = yield select(
    (state: RootState) => state.appsettings,
  )
  yield call(mapAuthProviders[authProvider].logout)
  yield put({
    type: 'user/SET_STATE',
    payload: {
      id: '',
      name: '',
      role: '',
      email: '',
      avatar: '',
      authorized: false,
      workspace: null,
      loading: false,
      loginProcessing: false,
    },
  })

  yield put({
    type: 'menu/RELOAD',
    payload: { loading: false },
  })

  console.log(' --- location --- ', history.location)

  // OfficeChat webview에서는 재로그인이 불가하므로 재실행 안내 화면으로 보낸다 (OFM-229)
  let targetURL: string | null = getLoginFailureRedirect()
  if (action.payload != null) {
    const { target } = action.payload
    if (target != null) {
      targetURL = target
    }
  } else if (history.location.pathname.indexOf('/outlook/') === 0) {
    // outlook plugin 상태임.
    const outlookUser = getEnvParam('userEmail') as string
    const curPath = window.encodeURIComponent(history.location.pathname + history.location.search)
    targetURL = `/auth/login?email=${outlookUser}&path=${curPath}`
  } else if (
    history.location.pathname.indexOf('/auth/login') === 0 ||
    history.location.pathname.indexOf('/auth/entry') === 0 ||
    history.location.pathname.indexOf('/auth/callback') === 0 ||
    history.location.pathname.indexOf('/auth/redirect') === 0 ||
    history.location.pathname.indexOf('/auth/restart-required') === 0
  ) {
    targetURL = null
  }

  if (targetURL != null) {
    yield call(history.push, `${targetURL}`)
  }
}

export default function* rootSaga() {
  if (window.parent === window) {
    yield all([
      takeLeading(actions.LOGIN, LOGIN),
      takeLeading(actions.LOGIN_BY_KEY, LOGIN_BY_KEY),
      takeLeading(actions.LOGIN_BY_TOKEN, LOGIN_BY_TOKEN),
      takeLeading(actions.LOGIN_BY_ENTRY_CHECK, LOGIN_BY_ENTRY_CHECK),
      takeLeading(actions.LOGIN_BY_ENTRY, LOGIN_BY_ENTRY),
      takeEvery(actions.REGISTER, REGISTER),
      takeLatest(actions.LOAD_CURRENT_ACCOUNT, LOAD_CURRENT_ACCOUNT),
      takeLeading(actions.LOGOUT, LOGOUT),
      call(LOAD_CURRENT_ACCOUNT), // run once on app load to check user auth
    ])
  }
}
